import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as verificationCreditApi from '../Api/verificationCreditApi';

const CREDITS_KEY = 'verificationCredits';
const JOBS_KEY = 'emailVerificationJobs';

export const useVerificationCreditProducts = () =>
	useQuery({
		queryKey: [CREDITS_KEY, 'products'],
		queryFn: () =>
			verificationCreditApi
				.getVerificationCreditProducts()
				.then((res) => res.data?.data || []),
		staleTime: 5 * 60 * 1000,
	});

export const useVerificationCreditSummary = (companyId) =>
	useQuery({
		queryKey: [CREDITS_KEY, 'summary', companyId],
		queryFn: () =>
			verificationCreditApi
				.getVerificationCreditSummary(companyId)
				.then((res) => res.data?.data),
		enabled: !!companyId,
		staleTime: 30 * 1000,
	});

export const useVerificationCreditPaymentIntent = () =>
	useMutation({
		mutationFn: (payload) =>
			verificationCreditApi
				.createVerificationCreditPaymentIntent(payload)
				.then((res) => res.data?.data),
	});

export const useConfirmVerificationCreditPurchase = (companyId) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload) =>
			verificationCreditApi
				.confirmVerificationCreditPurchase(payload)
				.then((res) => res.data?.data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [CREDITS_KEY, 'summary', companyId] });
		},
	});
};

export const useEmailVerificationLimits = (companyId) =>
	useQuery({
		queryKey: [JOBS_KEY, 'limits', companyId || 'platform'],
		queryFn: () =>
			verificationCreditApi
				.getEmailVerificationLimits(companyId)
				.then((res) => res.data?.data),
		staleTime: 60 * 1000,
	});

export const useUpdateEmailVerificationProvider = (companyId) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (provider) =>
			verificationCreditApi
				.updateEmailVerificationProvider({ companyId, provider })
				.then((res) => res.data?.data),
		onSuccess: (data) => {
			queryClient.setQueryData([JOBS_KEY, 'limits', companyId || 'platform'], data);
			queryClient.invalidateQueries({
				queryKey: [JOBS_KEY, 'limits', companyId || 'platform'],
			});
		},
	});
};

export const useVerifyEmailList = (companyId) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload) => {
			const listId = typeof payload === 'string' ? payload : payload?.listId;
			const provider = typeof payload === 'string' ? undefined : payload?.provider;
			return verificationCreditApi
				.verifyEmailList({ companyId, listId, provider })
				.then((res) => res.data?.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [CREDITS_KEY, 'summary', companyId] });
			queryClient.invalidateQueries({ queryKey: [JOBS_KEY, companyId] });
			queryClient.invalidateQueries({ queryKey: ['folders'] });
			queryClient.invalidateQueries({ queryKey: ['lists'] });
			queryClient.invalidateQueries({ queryKey: ['contacts'] });
		},
	});
};

export const useVerificationJobs = (companyId, limit = 20) =>
	useQuery({
		queryKey: [JOBS_KEY, companyId, limit],
		queryFn: () =>
			verificationCreditApi
				.getVerificationJobs(companyId, limit)
				.then((res) => res.data?.data || []),
		enabled: !!companyId,
	});

export const useVerificationJob = (companyId, jobId) =>
	useQuery({
		queryKey: [JOBS_KEY, companyId, jobId],
		queryFn: () =>
			verificationCreditApi
				.getVerificationJob(companyId, jobId)
				.then((res) => res.data?.data),
		enabled: !!companyId && !!jobId,
	});

export const useRemoveVerificationJobContacts = (companyId) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ jobId, mode, resultIds }) =>
			verificationCreditApi
				.removeVerificationJobContacts({ companyId, jobId, mode, resultIds })
				.then((res) => res.data?.data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: [JOBS_KEY, companyId] });
			if (variables?.jobId) {
				queryClient.invalidateQueries({
					queryKey: [JOBS_KEY, companyId, variables.jobId],
				});
			}
			queryClient.invalidateQueries({ queryKey: ['lists'] });
			queryClient.invalidateQueries({ queryKey: ['contacts'] });
			queryClient.invalidateQueries({ queryKey: ['folders'] });
		},
	});
};
